---
title: >-
  You can't spell Team without me - Selbsterkenntnis als Schlüssel zum
  Teambuilding
date: '2018-03-11'
excerpt: >-
  Selbsterkenntnis ist der Schlüssel zum erfolgreichen Teambuilding. Warum echte
  Teams mehr sind als Gruppen und welche Rolle Vertrauen, Motivation und
  Persönlichkeitsmodelle im Aufbau eines Teams spielen.
metaTitle: Selbsterkenntnis im Teambuilding - EmpowerMensch
metaDescription: >-
  Warum Selbsterkenntnis und Vertrauen zentrale Faktoren für Teambuilding sind –
  ein Praxisartikel von Kevin Rassner über Teams, Motivation und
  Persönlichkeitsmodelle.
language: de
slug: selbsterkenntnis-teambuilding
author: Kevin Rassner
---

Viele Organisationen teilen ihre Mitarbeiter standardmäßig in Teams ein und verkennen somit die Besonderheit, die ein Team ausmacht. Teams sind autonom agierende Gruppen, welche hocheffizient auf ein gemeinsames Ziel hinarbeiten. Fehlt einer dieser Punkte, so handelt es sich bestenfalls um eine Gruppe, kein Team.

Ein paar prominente Beispiele von echten Teams findet man in der Welt des Sports. Da wären die Golden State Warriors, welche die erste Hälfte der Saison 2015-2016 sogar ohne ihren Cheftrainer auskommen mussten und dennoch unter anderem den Rekord für die meisten Siege in einer Saison einstellten. Das für eine solche Leistung ein echter Teamzusammenhalt nötig ist, ist vermutlich allen bewusst.

Doch wie entsteht ein solches Team und gibt es Möglichkeiten, durch gezielte Maßnahmen einer Gruppe Menschen beim Wandel zu einem hochperformanten Team zu verhelfen?

> Teams are highly efficient, autonomous groups. Almost no so-called team that we know from our daily work qualifies as an actual team  
Prof. Kenneth Murrell, Privatgespräch, 2016

## Wie entstehen Teams?

Der Psychologe und Autor Jack Gibb beschrieb 1964 ein Modell, nach welchem der Teambuilding-Prozess aus vier essentiellen Teilen besteht:

1. Vertrauen innerhalb der Gruppe schaffen
2. Offene Kommunikation etablieren
3. Gemeinsame Ziele finden
4. Strukturen definieren, welche dem Team helfen, diese Ziele zu erreichen

In einigen meiner Blogartikel gehe ich auf diese vier Stufen ein und zeige Umsetzungsbeispiele. In diesem Artikel geht es um den ersten Schritt: Das Vertrauen innerhalb der Gruppe.

## Wie erschafft man ein vertrauensvolles Umfeld?

1. Vertrauen entsteht durch erkennen und erkannt werden. Wenn wir wissen, wie unser Gegenüber handeln wird, dann fangen wir an, diesem zu trauen.
2. Dieser Prozess ist keine Einbahnstraße. Es ist essentiell, die Hand zu reichen und einen Vertrauensvorschuss zu geben.

> The person who dares to entrust himself to others goes far in creating a climate of trust in the group  
Jack Gibb, Psychologe

In meinem Team wollte ich die Möglichkeit schaffen, dass sich alle kennenlernen und auch Vertrauensvorschüsse liefern.

## Selbsterkenntnis als erster Schritt zur Erkennung vom Gegenüber

Wir müssen wissen, wie wir selbst funktionieren, um einzuschätzen, wie Handlungen des Gegenübers auf uns wirken. Zudem müssen wir wissen, wie der andere funktioniert, um die Motive hinter Handlungen zu erkennen. Dafür ist Wissen notwendig: Über sich selbst, über das Gegenüber, und theoretisches Wissen über menschliche Funktionsweise. In regelmäßigen Workshops vermittle ich daher genau das.

## Organizational Behavior 101

Da ich überzeugt bin, dass die Selbsterkenntnis die Wurzel effektiver Zusammenarbeit ist, gebe ich meinen Teammitgliedern Unterstützung dabei.

Zu Beginn der Workshops geht es um Motivation und Persönlichkeitstypen. Besonders hilfreich sind die Modelle von Herzberg und McClelland.

## Von Arschtritten und Hygiene

Frederik Herzberg unterscheidet in seinem Paper „One More Time – How do you motivate employees“ zwischen negativen physischen KITA, negativen psychologischen KITA und positiven KITA (Belohnungen). Auch positive KITA, wie Belohnungen, bringen nicht langfristige Motivation, sondern nur Bewegung.

Herzberg fand heraus, dass es nicht nur „unmotiviert und motiviert“ gibt, sondern eine Achse mit vier Zuständen:

- Unmotiviert
- Nicht unmotiviert
- Nicht motiviert
- Motiviert

Hygienefaktoren wie Geld, Jobsicherheit und Status bringen einen nur von „unmotiviert“ zu „nicht unmotiviert“. Echte Motivatoren wie Selbstverwirklichung, Anerkennung und Sinnhaftigkeit führen zu echter Motivation.

## Was Trump und Franz von Assisi gemeinsam haben

David McClelland fand heraus, dass nicht alle Menschen die gleichen Motivatoren besitzen. Während manche Erfolg anstreben, zielen andere auf Macht ab, und wieder anderen ist Zugehörigkeit wichtig. Diese Erkenntnisse helfen als Startpunkt für Selbstreflexion und werden im Workshop betrachtet.

## Persönlichkeitstests und was davon zu halten ist

Wir besprechen außerdem Persönlichkeitstests wie den [Myers-Briggs-Typenindikator](/blog/team-building-2025-neubewertung#the-personality-testing-problem). Diese Ergebnisse sind kritisch zu betrachten und bieten einen Startpunkt zur eigenen Charakterreflexion.

## Eustress und Distress

Auch Stress wird thematisiert: Eustress (positiver Stress) und Distress (negativer Stress). Das Verständnis über die Auswirkungen von Stress hilft nicht nur Kollegen zu warnen, sondern auch effektiver zusammenzuarbeiten.

## Trust the Process

Der [Teambuilding-Prozess](/blog/team-building-2025-neubewertung) lässt sich auch ohne dedizierte Workshops befeuern. Durch kleine Vertrauensvorschüsse im Meeting-Kontext, beispielsweise eine persönliche Anekdote zum Einstieg, wird ein offeneres Klima geschaffen.

Auch in vertrauteren Umfeldern helfen solche Vorlagen, die Teamdynamik zu fördern und das Verständnis untereinander zu vertiefen.


